import { inject, Inject, Injectable, InjectionToken } from '@angular/core';
import {
	Environment,
	ENVIRONMENT,
	HttpParamsBuilder,
	Meta,
	Response,
	Vehicle,
} from '@agilox/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * A drop-in replacement for the union select's own vehicle fetching. Provide it
 * to feed the select from another source (e.g. the MaaS overview backs it with
 * `MaasService.fetchVehicles`, scoping the list to MaaS-subscribed vehicles).
 * When absent the select falls back to the fleet-wide machineMgmt endpoint.
 */
export type VehicleUnionSelectFetcher = (
	search: string,
	meta: Meta,
	union?: string,
	serials?: string[]
) => Observable<Response<Vehicle>>;

export const VEHICLE_UNION_SELECT_FETCHER = new InjectionToken<VehicleUnionSelectFetcher>(
	'VEHICLE_UNION_SELECT_FETCHER'
);

@Injectable()
export class VehicleUnionSelectService {
	@Inject(ENVIRONMENT) public environment: Environment = inject(ENVIRONMENT);
	private http: HttpClient = inject(HttpClient);
	private fetcher = inject(VEHICLE_UNION_SELECT_FETCHER, { optional: true });

	public fetchVehicles(
		search: string,
		meta: Meta,
		union?: string,
		serials?: string[]
	): Observable<Response<Vehicle>> {
		if (this.fetcher) {
			return this.fetcher(search, meta, union, serials);
		}

		return this.fetchFromMachineMgmt(search, meta, union, serials);
	}

	private fetchFromMachineMgmt(
		search: string,
		meta: Meta,
		union?: string,
		serials?: string[]
	): Observable<Response<Vehicle>> {
		let params: HttpParams = HttpParamsBuilder.buildParameters(meta, undefined, search, undefined);
		if (!search) {
			/**
			 * If the search string is not empty
			 * we use the prioritize parameter
			 */
			if (union) {
				params = params.append('prioritize[unionUuid]', union);
			} else if (serials?.length) {
				params = params.append('prioritize[serial]', serials.join(','));
			}
		}

		return this.http.get<Response<Vehicle>>(this.environment.server + '/v2/machineMgmt/vehicles', {
			params,
		});
	}
}
